import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { errorText } from "@/lib/forms"
import {
  useInviteToOrgMutation,
  useInviteToPortfolioMutation,
} from "@/v2/graphql/public-generated-rq"
import { useOrgSlugBridge } from "@/v2/hooks/useOrgSlugBridge"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, FormField, Input, Modal, useToast } from "@lumen-energy/design-system"

const schema = z.object({
  name: z.string().min(1, "Please enter a name"),
  email: z.string().email("Please enter a valid email"),
})

export const InviteUserModal = ({
  entityName,
  portfolioPid,
}: {
  entityName: string
  portfolioPid: string | null
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  })
  const inviteToOrg = useInviteToOrgMutation()
  const inviteToPortfolio = useInviteToPortfolioMutation()
  const toast = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const orgSlug = useOrgSlugBridge()

  const onSubmit = async (data: typeof schema._type) => {
    if (!orgSlug) return
    setIsOpen(false)
    const promise = (
      portfolioPid
        ? inviteToPortfolio.mutateAsync({ input: { orgSlug, portfolioPid, ...data } })
        : inviteToOrg.mutateAsync({ input: { orgSlug, ...data } })
    ).then(() => reset())
    toast.promise(promise, {
      loading: `Inviting ${data.name}`,
      error: (error) => `${error}`,
      success: `Invited ${data.name}`,
    })
    await promise
  }

  return (
    <Modal.Root onOpenChange={(isOpen) => setIsOpen(isOpen)} open={isOpen}>
      <Modal.Trigger asChild data-testid="open-invite-modal">
        <Button className="whitespace-nowrap">Invite user</Button>
      </Modal.Trigger>
      <Modal.Content
        title="Invite user"
        description={`This user will see all the properties belonging to ${entityName}`}
      >
        <Modal.Body>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <FormField
                id="name"
                label="Full name"
                errorText={errorText(errors.name)}
                required
              >
                <Input
                  id="name"
                  placeholder="Enter first and last name"
                  autoFocus
                  {...register("name")}
                />
              </FormField>
              <FormField
                id="email"
                label="Email"
                errorText={errorText(errors.email)}
                required
              >
                <Input
                  id="email"
                  placeholder="name@example.com"
                  {...register("email")}
                />
              </FormField>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                type="submit"
                loading={inviteToOrg.isPending || inviteToPortfolio.isPending}
                data-testid="submit-invite"
              >
                Invite user
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
}
